import { ChildProgramComponentInput, ComponentTypeEnum } from "@/graphql";
import { ComponentInput, MainComponent } from "@/types/programs";
import { areDifferentTimes } from "@/utils/datetime";

type ProgramComponent = (MainComponent | ChildProgramComponentInput) & {
  targetDueDate?: string | null;
};

export const validateComponentDate = (
  input: ComponentInput,
  programComponent?: ProgramComponent,
) => {
  const { targetStartDate, targetEndDate, targetDueDate, type } = input;
  const {
    targetStartDate: startDateString,
    targetEndDate: endDateString,
    targetDueDate: dueDateString,
  } = programComponent || {};

  const currentStartDate = startDateString ? new Date(startDateString) : null;
  const currentEndDate = endDateString ? new Date(endDateString) : null;
  const currentDueDate = dueDateString ? new Date(dueDateString) : null;

  const newStartDate = targetStartDate ? new Date(targetStartDate) : null;
  const newEndDate = targetEndDate ? new Date(targetEndDate) : null;
  const newDueDate = targetDueDate ? new Date(targetDueDate) : null;

  const dueDateRequired =
    type &&
    [
      ComponentTypeEnum.Survey,
      ComponentTypeEnum.ActionItem,
      ComponentTypeEnum.Video,
      ComponentTypeEnum.Article,
    ].includes(type);

  const isGroupMeeting = type === ComponentTypeEnum.GroupMeeting;

  const hasChangeStartDate = currentStartDate
    ? areDifferentTimes(newStartDate, currentStartDate)
    : false;

  const hasChangeDueDate = currentDueDate
    ? areDifferentTimes(newDueDate, currentDueDate)
    : false;

  const hasChangeEndDate = currentEndDate
    ? areDifferentTimes(newEndDate, currentEndDate)
    : false;

  const hasChangeData = {
    hasChangeDueDate,
    hasChangeEndDate,
    hasChangeStartDate,
  };

  // Check date sequence validity
  if (
    newStartDate &&
    newEndDate &&
    (newStartDate >= newEndDate ||
      (dueDateRequired && newDueDate && newStartDate >= newDueDate))
  ) {
    return {
      ...hasChangeData,
      valid: false,
      errorType: "targetStartDate",
      message:
        "The target launch date must be before the target close date and due date.",
    };
  }

  if (dueDateRequired && newDueDate && newEndDate && newDueDate > newEndDate) {
    return {
      ...hasChangeData,
      valid: false,
      errorType: "targetDueDate",
      message:
        "The target due date must be on or before the target close date.",
    };
  }

  // Helper function for date validation logic
  function isDateInvalid({
    newDate,
    currentDate,
  }: {
    newDate: Date | null;
    currentDate: Date | null;
  }): boolean {
    // Skip validation if dates haven't changed
    if (currentDate && newDate && !areDifferentTimes(newDate, currentDate))
      return false;

    // Check if new date is in the past
    return !!newDate && newDate < new Date();
  }

  // Validate start date
  if (isDateInvalid({ newDate: newStartDate, currentDate: currentStartDate })) {
    return {
      ...hasChangeData,
      valid: false,
      errorType: "targetStartDate",
      message: isGroupMeeting
        ? "The meeting time must be in the future."
        : "The target launch date must be in the future.",
    };
  }

  // Validate end date
  if (isDateInvalid({ newDate: newEndDate, currentDate: currentEndDate })) {
    return {
      ...hasChangeData,
      valid: false,
      errorType: "targetEndDate",
      message: "The target close date must be in the future.",
    };
  }

  // Validate due date if required
  if (
    dueDateRequired &&
    isDateInvalid({ newDate: newDueDate, currentDate: currentDueDate })
  )
    return {
      ...hasChangeData,
      valid: false,
      errorType: "targetDueDate",
      message: "The target due date must be in the future.",
    };

  return { ...hasChangeData, valid: true, errorType: null, message: null };
};
